import { InputHTMLAttributes, forwardRef } from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

interface FormInputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: LucideIcon;
  label?: string;
  error?: string;
  /** Content to render at the right side of the input (e.g. show/hide password button) */
  suffix?: React.ReactNode;
}

const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  ({ icon: Icon, label, error, suffix, id, className, ...props }, ref) => {
    return (
      <div>
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-muted-foreground mb-1.5">
            {label}
          </label>
        )}
        <div className="relative">
          {Icon && (
            <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-muted-foreground/60" />
          )}
          <input
            ref={ref}
            id={id}
            className={cn(
              'w-full py-3 bg-background/60 border border-border rounded-xl text-foreground placeholder-muted-foreground/50',
              'focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/50',
              'transition-colors',
              Icon && 'pl-11',
              !Icon && 'pl-4',
              suffix ? 'pr-12' : 'pr-4',
              error && 'border-destructive focus:border-destructive focus:ring-destructive/50',
              className,
            )}
            {...props}
          />
          {suffix && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {suffix}
            </div>
          )}
        </div>
        {error && (
          <p className="mt-1.5 text-sm text-destructive">{error}</p>
        )}
      </div>
    );
  }
);

FormInput.displayName = 'FormInput';

export default FormInput;
